export const NOTIFICATION_TYPES = {
  FOLLOW: 'follow',
  JOIN_REQUEST: 'join_request',
  MEMBER_JOINED: 'member_joined',
  JOIN_APPROVED: 'join_approved',
  JOIN_DENIED: 'join_denied',
  GROUP_MESSAGE: 'group_message',
  DM_MESSAGE: 'dm_message',
  QUEUE_MATCHED: 'queue_matched',
  PROMOTED_TO_MANAGER: 'promoted_to_manager',
  GROUP_EXPIRING: 'group_expiring',
};

export const GROUP_NOTIFICATION_TYPES = [
  NOTIFICATION_TYPES.JOIN_REQUEST,
  NOTIFICATION_TYPES.MEMBER_JOINED,
  NOTIFICATION_TYPES.JOIN_APPROVED,
  NOTIFICATION_TYPES.JOIN_DENIED,
  NOTIFICATION_TYPES.GROUP_MESSAGE,
  NOTIFICATION_TYPES.QUEUE_MATCHED,
  NOTIFICATION_TYPES.PROMOTED_TO_MANAGER,
  NOTIFICATION_TYPES.GROUP_EXPIRING,
];

export const PEOPLE_NOTIFICATION_TYPES = [NOTIFICATION_TYPES.FOLLOW];
export const DM_NOTIFICATION_TYPES = [NOTIFICATION_TYPES.DM_MESSAGE];
export const EVENT_NOTIFICATION_TYPES = [];
export const ALLOWED_NOTIFICATION_TYPES = [
  ...GROUP_NOTIFICATION_TYPES,
  ...PEOPLE_NOTIFICATION_TYPES,
  ...DM_NOTIFICATION_TYPES,
];

export function getNotificationTarget(notification) {
  if (!notification) return null;

  if (
    notification.type === NOTIFICATION_TYPES.DM_MESSAGE &&
    notification.related_dm_conversation_id
  ) {
    return `/chats/dm/${notification.related_dm_conversation_id}`;
  }

  if (notification.type === NOTIFICATION_TYPES.FOLLOW && notification.related_user_id) {
    return `/profile/${notification.related_user_id}`;
  }

  if (
    [
      NOTIFICATION_TYPES.MEMBER_JOINED,
      NOTIFICATION_TYPES.JOIN_REQUEST,
      NOTIFICATION_TYPES.JOIN_APPROVED,
      NOTIFICATION_TYPES.PROMOTED_TO_MANAGER,
      NOTIFICATION_TYPES.GROUP_EXPIRING,
      NOTIFICATION_TYPES.GROUP_MESSAGE,
      NOTIFICATION_TYPES.QUEUE_MATCHED,
    ].includes(notification.type) &&
    notification.related_group_id
  ) {
    return `/groups/${notification.related_group_id}`;
  }

  if (notification.type === NOTIFICATION_TYPES.JOIN_DENIED && notification.related_event_id) {
    return `/?event=${notification.related_event_id}`;
  }

  if (notification.related_group_id) return `/groups/${notification.related_group_id}`;
  if (notification.related_event_id) return `/?event=${notification.related_event_id}`;
  if (notification.related_user_id) return `/profile/${notification.related_user_id}`;
  return null;
}

export function getNotificationDestinationFilter(notification) {
  if (!notification) return null;
  if (notification.related_dm_conversation_id) {
    return { column: 'related_dm_conversation_id', value: notification.related_dm_conversation_id };
  }
  if (notification.related_group_id) {
    return { column: 'related_group_id', value: notification.related_group_id };
  }
  if (notification.related_user_id) {
    return { column: 'related_user_id', value: notification.related_user_id };
  }
  if (notification.related_event_id) {
    return { column: 'related_event_id', value: notification.related_event_id };
  }
  return null;
}

export function buildGroupMessageTitle(groupName, count) {
  const safeCount = Math.max(1, Number(count) || 1);
  return `${safeCount} new message${safeCount === 1 ? '' : 's'} in ${groupName || 'your group'}`;
}

export function parseGroupMessageCount(title) {
  const match = String(title || '').match(/^(\d+)\s+new message/);
  return match ? Number(match[1]) : 0;
}

export function buildDmMessageTitle(displayName, count) {
  const safeCount = Math.max(1, Number(count) || 1);
  return `${safeCount} new message${safeCount === 1 ? '' : 's'} from ${displayName || 'Someone'}`;
}

export function parseDmMessageCount(title) {
  return parseGroupMessageCount(title);
}

export async function markGroupMessageNotificationsRead({ supabase, userId, groupId }) {
  if (!userId || !groupId) return;
  await supabase
    .from('notifications')
    .update({ read: true })
    .eq('user_id', userId)
    .eq('type', NOTIFICATION_TYPES.GROUP_MESSAGE)
    .eq('related_group_id', groupId)
    .eq('read', false);
}

export async function markDmConversationNotificationsRead({ supabase, userId, conversationId }) {
  if (!userId || !conversationId) return;
  await supabase
    .from('notifications')
    .update({ read: true })
    .eq('user_id', userId)
    .eq('type', NOTIFICATION_TYPES.DM_MESSAGE)
    .eq('related_dm_conversation_id', conversationId)
    .eq('read', false);
}

export async function upsertGroupMessageNotifications({
  supabase,
  recipientIds,
  senderId,
  groupId,
  groupName,
  messagePreview,
}) {
  const targets = [...new Set((recipientIds || []).filter((id) => id && id !== senderId))];
  if (targets.length === 0 || !groupId) return;

  const { data: existingUnread } = await supabase
    .from('notifications')
    .select('id, user_id, title')
    .eq('type', NOTIFICATION_TYPES.GROUP_MESSAGE)
    .eq('related_group_id', groupId)
    .eq('read', false)
    .in('user_id', targets);

  const existingByUser = new Map();
  (existingUnread || []).forEach((row) => {
    if (!existingByUser.has(row.user_id)) {
      existingByUser.set(row.user_id, row);
    }
  });

  const now = new Date().toISOString();
  const updates = [];
  const inserts = [];

  targets.forEach((userId) => {
    const existing = existingByUser.get(userId);
    if (existing) {
      const currentCount = parseGroupMessageCount(existing.title);
      const nextCount = Math.max(1, currentCount + 1);
      updates.push(
        supabase
          .from('notifications')
          .update({
            title: buildGroupMessageTitle(groupName, nextCount),
            message: messagePreview || null,
            related_user_id: senderId || null,
            created_at: now,
          })
          .eq('id', existing.id),
      );
    } else {
      inserts.push({
        user_id: userId,
        type: NOTIFICATION_TYPES.GROUP_MESSAGE,
        title: buildGroupMessageTitle(groupName, 1),
        message: messagePreview || null,
        related_group_id: groupId,
        related_user_id: senderId || null,
        read: false,
        created_at: now,
      });
    }
  });

  if (updates.length > 0) {
    await Promise.all(updates);
  }
  if (inserts.length > 0) {
    await supabase.from('notifications').insert(inserts);
  }
}

export async function upsertDmMessageNotification({
  supabase,
  recipientUserId,
  senderId,
  senderName,
  conversationId,
  messagePreview,
}) {
  if (!recipientUserId || !senderId || !conversationId || recipientUserId === senderId) return;

  const preview = (messagePreview || '').slice(0, 120);
  const now = new Date().toISOString();

  const { data: existing } = await supabase
    .from('notifications')
    .select('id, title')
    .eq('user_id', recipientUserId)
    .eq('type', NOTIFICATION_TYPES.DM_MESSAGE)
    .eq('related_user_id', senderId)
    .eq('related_dm_conversation_id', conversationId)
    .eq('read', false)
    .maybeSingle();

  if (existing?.id) {
    const currentCount = parseDmMessageCount(existing.title);
    const nextCount = Math.max(1, currentCount + 1);
    await supabase
      .from('notifications')
      .update({
        title: buildDmMessageTitle(senderName, nextCount),
        message: preview || null,
        created_at: now,
      })
      .eq('id', existing.id);
    return;
  }

  await supabase.from('notifications').insert({
    user_id: recipientUserId,
    type: NOTIFICATION_TYPES.DM_MESSAGE,
    title: buildDmMessageTitle(senderName, 1),
    message: preview || null,
    related_user_id: senderId,
    related_dm_conversation_id: conversationId,
    read: false,
    created_at: now,
  });
}
